/**
 * File: assets/basari-most-popular-products.js
 * Purpose: Horizontal carousel keyboard/smooth scroll + Buy Now / Quick Add
 *          for the Most Popular Products section.
 * Notes:
 *   - Reuses Dawn cart-drawer / cart-notification when present.
 *   - Wishlist uses the existing PLP card hooks (data-basari-product-card-wishlist /
 *     js-basari-wishlist). No second wishlist system is introduced here.
 *   - No external libraries.
 */
(function () {
  'use strict';

  function getCartUi() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function publishCartUpdate(cartData, variantId) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'basari-most-popular-products',
        productVariantId: variantId,
        cartData: cartData,
      });
    }
  }

  function addToCart(variantId, options) {
    var opts = options || {};
    var cart = getCartUi();
    var config =
      typeof fetchConfig === 'function'
        ? fetchConfig('javascript')
        : {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          };

    config.headers = config.headers || {};
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.headers['Accept'] = 'application/javascript';

    var body = {
      id: String(variantId),
      quantity: 1,
    };

    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map(function (section) {
        return section.id;
      });
      body.sections_url = window.location.pathname;
    }

    config.body = JSON.stringify(body);

    var cartAddUrl =
      (window.routes && window.routes.cart_add_url) || '/cart/add';

    return fetch(cartAddUrl, config)
      .then(function (response) {
        return response.json();
      })
      .then(function (response) {
        if (response.status) {
          throw new Error(response.description || response.message || 'Unable to add to cart');
        }

        if (opts.checkout) {
          window.location.href = '/checkout';
          return response;
        }

        if (!cart) {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
          return response;
        }

        publishCartUpdate(response, variantId);

        if (typeof cart.renderContents === 'function') {
          cart.renderContents(response);
        }

        if (cart.classList && cart.classList.contains('is-empty')) {
          cart.classList.remove('is-empty');
        }

        return response;
      });
  }

  function setLoading(el, loading) {
    if (!el) return;
    el.classList.toggle('is-loading', loading);
    if (loading) {
      el.setAttribute('aria-disabled', 'true');
    } else {
      el.removeAttribute('aria-disabled');
    }
  }

  function getCardFromEvent(target) {
    return target && target.closest ? target.closest('[data-basari-mpp-card]') : null;
  }

  /**
   * Align PLP card wishlist buttons with the theme's shared wishlist hooks
   * (same markers used by snippets/wishlist-button.liquid).
   */
  function bridgeWishlistHooks(root) {
    root.querySelectorAll('[data-basari-product-card-wishlist]').forEach(function (btn) {
      var card = getCardFromEvent(btn);
      btn.classList.add('js-basari-wishlist');
      if (!btn.hasAttribute('data-basari-wishlist-button')) {
        btn.setAttribute('data-basari-wishlist-button', '');
      }
      if (card && card.getAttribute('data-product-id')) {
        btn.setAttribute('data-product-id', card.getAttribute('data-product-id'));
      }
      if (!btn.hasAttribute('aria-pressed')) {
        btn.setAttribute('aria-pressed', 'false');
      }
    });
  }

  function initInteractions(root) {
    bridgeWishlistHooks(root);

    root.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      /* Wishlist: leave to existing theme / Wishlist Monk hooks — do not handle here */

      var cartBtn = target.closest('[data-basari-product-card-cart]');
      if (cartBtn && root.contains(cartBtn)) {
        event.preventDefault();
        var cartCard = getCardFromEvent(cartBtn);
        if (!cartCard || cartCard.getAttribute('data-product-available') === 'false') return;
        var cartVariant = cartCard.getAttribute('data-variant-id');
        if (!cartVariant) return;

        setLoading(cartBtn, true);
        addToCart(cartVariant, { checkout: false })
          .catch(function (err) {
            console.error(err);
          })
          .finally(function () {
            setLoading(cartBtn, false);
          });
        return;
      }

      var buyNow = target.closest('.basari-product-card__cta');
      if (buyNow && root.contains(buyNow)) {
        var buyCard = getCardFromEvent(buyNow);
        if (!buyCard || buyCard.getAttribute('data-product-available') === 'false') return;
        var buyVariant = buyCard.getAttribute('data-variant-id');
        if (!buyVariant) return;

        event.preventDefault();
        setLoading(buyNow, true);
        addToCart(buyVariant, { checkout: true }).catch(function (err) {
          console.error(err);
          setLoading(buyNow, false);
          var url = buyCard.getAttribute('data-product-url');
          if (url) window.location.href = url;
        });
      }
    });
  }

  function initCarousel(root) {
    var track = root.querySelector('[data-basari-mpp-track]');
    if (!track) return;

    track.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;

      var slide = track.querySelector('[data-basari-mpp-slide]');
      if (!slide) return;

      var amount = slide.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).gap) || 0);
      event.preventDefault();
      track.scrollBy({
        left: event.key === 'ArrowRight' ? amount : -amount,
        behavior: 'smooth',
      });
    });
  }

  function initSection(root) {
    if (!root || root.dataset.basariMppReady === 'true') return;
    root.dataset.basariMppReady = 'true';
    initCarousel(root);
    initInteractions(root);
  }

  function initAll() {
    document.querySelectorAll('[data-basari-mpp]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target && event.target.querySelector
      ? event.target.querySelector('[data-basari-mpp]')
      : null;
    if (!root && event.target && event.target.matches && event.target.matches('[data-basari-mpp]')) {
      root = event.target;
    }
    if (root) {
      delete root.dataset.basariMppReady;
      initSection(root);
    }
  });
})();
